import { useEffect, useState } from 'react';
import type { FilaResumen } from '../../api/ops/citas';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { TarjetaCifra } from '../../componentes/TarjetaCifra';
import { rolesDe } from '../../logica/acceso';
import { estadoDeError } from '../../logica/estados';
import { hoyEn, sumarDias } from '../../logica/horario';
import { useCabecera } from '../../marco/cabecera';
import type { PropsPantalla } from '../tipos';
import { Barra, type Vista } from './Barra';
import { lunesDe, relativo } from './logica';
import { AYUDA } from './textos';

/**
 * El Socio en la Agenda (9.1): solo cifras (`getAppointmentSummary`, `citas.agenda.ver_agregado`), por día o por semana y
 * por profesional, sin datos de los clientes. El nombre de cada profesional llega en la fila (`professional_display_name`,
 * G845), también para el Socio.
 */
const COLORES = ['var(--rv-mesa-azul)', 'var(--rv-mesa-naranja)', 'var(--rv-mesa-morado)', 'var(--rv-mesa-verde)', 'var(--rv-mesa-rosa)'];

export function Cifras(props: PropsPantalla) {
    const { ops, negocio, movil, membresia, nombreNegocio, navegar } = props;
    const zona = membresia?.timezone ?? 'Europe/Madrid';
    const hoy = hoyEn(zona);
    const [fecha, setFecha] = useState(hoy);
    const [vista, setVista] = useState<Vista>('dia');
    const [filas, setFilas] = useState<FilaResumen[] | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [tabla, setTabla] = useState(false);
    useCabecera({ ayuda: AYUDA.socio, acciones: [] });

    const desde = vista === 'semana' ? lunesDe(fecha) : fecha;
    const hasta = vista === 'semana' ? sumarDias(desde, 6) : fecha;

    useEffect(() => {
        let vivo = true;
        ops.getAppointmentSummary(negocio, desde, hasta)
            .then((r) => {
                if (vivo) {
                    setFilas(r.rows);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, desde, hasta, intento]);

    if (filas === null) {
        if (error !== null) {
            const e = estadoDeError(error);
            return e.tipo === 'sin-permiso' ? (
                <EstadoSinPermiso seccion="la Agenda" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />
            ) : (
                <EstadoError seccion="la agenda" codigoSoporte={e.codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={movil} />
            );
        }
        return <EstadoCargando seccion="la agenda" />;
    }

    // Una fila por profesional (suma de los días del periodo que da la API).
    const ids = [...new Set(filas.map((f) => f.professional_id))];
    const porPro = ids.map((id, i) => {
        const suyas = filas.filter((f) => f.professional_id === id);
        const s = (k: 'appointments' | 'booked_minutes' | 'completed' | 'no_show' | 'cancelled') => suyas.reduce((n, f) => n + f[k], 0);
        return { id, nombre: suyas[0]?.professional_display_name ?? `Profesional ${String(i + 1)}`, citas: s('appointments'), minutos: s('booked_minutes'), completadas: s('completed'), noShow: s('no_show'), canceladas: s('cancelled') };
    });
    const total = (k: 'citas' | 'minutos' | 'completadas' | 'noShow' | 'canceladas') => porPro.reduce((n, r) => n + r[k], 0);
    const horas = (m: number) => (m / 60).toLocaleString('es-ES', { maximumFractionDigits: 1 });
    const totalCitas = total('citas');
    const cuando = vista === 'semana' ? 'Esta semana' : relativo(fecha, hoy);
    const max = Math.max(1, ...porPro.map((r) => r.citas));

    return (
        <div className="rv-ag">
            <p className="rv-ag-sub">Las cifras de la agenda, por día y por profesional.</p>
            <Barra fecha={fecha} hoy={hoy} vista={vista} movil={movil} onFecha={setFecha} onVista={setVista} />
            <div className="rv-ag-cifras">
                <section className="rv-ag-barras" aria-label="Por profesional">
                    <h3>Por profesional</h3>
                    {totalCitas === 0 ? (
                        <p className="rv-ag-pie">No hay citas en este periodo</p>
                    ) : tabla ? (
                        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ textAlign: 'left' }}>
                                    <th>Profesional</th>
                                    <th>Citas</th>
                                    <th>Horas reservadas</th>
                                    <th>Completadas</th>
                                    <th>No presentados</th>
                                    <th>Canceladas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {porPro.map((r) => (
                                    <tr key={r.id}>
                                        <td>{r.nombre}</td>
                                        <td>{r.citas}</td>
                                        <td>{`${horas(r.minutos)} h`}</td>
                                        <td>{r.completadas}</td>
                                        <td>{r.noShow}</td>
                                        <td>{r.canceladas}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        porPro.map((r, i) => (
                            <div key={r.id} className="rv-ag-barra-pro" title={`${r.nombre} · ${horas(r.minutos)} h · ${String(r.completadas)} completadas · ${String(r.noShow)} no presentados · ${String(r.canceladas)} canceladas`}>
                                <div className="rv-ag-barra-pro__fila">
                                    <span>{r.nombre}</span>
                                    <strong>{r.citas === 1 ? '1 cita' : `${String(r.citas)} citas`}</strong>
                                    <em>{`${String(Math.round((r.citas / totalCitas) * 100))} %`}</em>
                                </div>
                                <div className="rv-ag-barra-pro__pista">
                                    <span style={{ width: `${String((r.citas / max) * 100)}%`, background: COLORES[i] ?? 'var(--rv-fg-4)' }} />
                                </div>
                            </div>
                        ))
                    )}
                    {totalCitas > 0 && (
                        <button type="button" className="rv-ag-b" style={{ alignSelf: 'flex-start', border: 0, padding: 0 }} onClick={() => { setTabla((t) => !t); }}>
                            <Icono nombre={tabla ? 'chart-column' : 'table'} />
                            <span>{tabla ? 'Ver como gráfico' : 'Ver como tabla'}</span>
                        </button>
                    )}
                </section>
                <div className="rv-ag-kpi">
                    <TarjetaCifra titulo="Citas" cifra={String(totalCitas)} pie={cuando} oscura />
                </div>
                <div className="rv-ag-kpi">
                    <TarjetaCifra titulo="Horas reservadas" cifra={horas(total('minutos'))} moneda="h" pie="Entre todos" />
                </div>
                <div className="rv-ag-kpi">
                    <TarjetaCifra titulo="Completadas" cifra={String(total('completadas'))} pie="Vinieron" />
                </div>
                <div className="rv-ag-kpi">
                    <TarjetaCifra titulo="No presentados" cifra={String(total('noShow'))} pie="Marcados por el equipo" />
                </div>
                <div className="rv-ag-kpi">
                    <TarjetaCifra titulo="Canceladas" cifra={String(total('canceladas'))} pie="Por la clienta o el negocio" />
                </div>
            </div>
            <p className="rv-ag-nota">
                <Icono nombre="eye" />
                Con tu rol ves solo cifras, sin datos de los clientes.
            </p>
        </div>
    );
}
